import React, { FC, memo } from "react";
import { IconButton, Input } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { CalculateButton } from "./CalculateButton";
import { NumberClassifierInput, Tooltip } from "./styled";

import { useSymbolSettingsContext } from "../../../../modules/Style/context/styleSettingsContext";
import { getParameterValue, isCalculatedParameter } from "../../../../evergis/core/classification/getFromClassified";
import { useNumberClassifierUpdate } from "../../../Classification/hooks/useNumberClassifierUpdate";
import { getSymbolParameterInfo } from "../../../Classification/getSymbolParameterInfo";
import { useExpressionAttribute } from "../../hooks/useExpressionAttribute";

import { CalculatedParameterCreator } from "../../../Classification/hooks/useCalculateByAttribute";
import { ClassificationParameter } from "../../../../interfaces/classification";
import { CalculatedAble } from "../../../../interfaces/style";

interface AngleClassifierProps {
  name: ClassificationParameter;
  value: CalculatedAble<number>;
  classifyAble?: boolean;
  onChange: (value: CalculatedAble<number>) => void;
}

const createCalculatedAngle: CalculatedParameterCreator = attributeOption => ({
  defaultValue: 0,
  type: "calculated",
  expression: attributeOption.value,
});

export const AngleClassifier: FC<AngleClassifierProps> = memo(({ name, value, classifyAble = true, onChange }) => {
  const { t } = useTranslation("common");
  const { ableClassify, inputWidth } = useSymbolSettingsContext();
  const { min, max, step, units, serialize } = getSymbolParameterInfo(name);
  const parameterValue = serialize(getParameterValue(value)) || 0;
  const expressionAttribute = useExpressionAttribute(value);

  const { handleChange, handleBlur, removeClassification } = useNumberClassifierUpdate({
    onChange,
    parameter: name,
    parameterValue,
  });

  return isCalculatedParameter(value) && expressionAttribute ? (
    <Tooltip content={expressionAttribute.alias || expressionAttribute.attributeName}>
      {ref1 => (
        <Input
          readOnly
          value={expressionAttribute.alias || expressionAttribute.attributeName}
          innerRef={ref1}
          width={"9.125rem"}
          style={{ cursor: "default", textOverflow: "ellipsis" }}
        >
          <Tooltip content={t("symbolEditor.removeClassifier")}>
            {ref2 => <IconButton innerRef={ref2} kind={"close"} onClick={removeClassification} />}
          </Tooltip>
        </Input>
      )}
    </Tooltip>
  ) : (
    <NumberClassifierInput
      value={parameterValue}
      width={inputWidth}
      onChange={handleChange}
      onBlur={handleBlur}
      step={step}
      min={min}
      max={max}
      units={units}
      title={`${parameterValue}`}
    >
      {ableClassify && classifyAble ? <CalculateButton mini name={name} creator={createCalculatedAngle} /> : null}
    </NumberClassifierInput>
  );
});
